'use client';

import { TRAVEL_TIME_TYPE } from '@/constants/travel-program';
import { differenceInSeconds, parse } from 'date-fns';
import type React from 'react';
import { useFormContext } from 'react-hook-form';
import { FaChevronRight } from 'react-icons/fa';

interface RuleItemProps {
	icon: React.ReactNode;
	title: string;
	value: string;
	onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const RuleItem: React.FC<RuleItemProps> = ({ icon, title, value, onClick }) => {
	const {
		watch,
		formState: { errors },
	} = useFormContext();
	const locationTypeValue = watch('locationType');
	const locationNameValue = watch('locationName');
	const locationAddressValue = watch('locationAddress');
	const locationRadiusValue = watch('locationRadius');

	const description = (title: string) => {
		const { watch } = useFormContext();
		const timeTypeValue = watch('timeType');
		const startTimeValue = watch('startTime');
		const endTimeValue = watch('endTime');
		const daysValue = watch('days');

		const startTime = parse(startTimeValue, 'h:mm a', new Date());
		const endTime = parse(endTimeValue, 'h:mm a', new Date());
		const timeDifference = differenceInSeconds(endTime, startTime) / 3600;

		switch (title) {
			case 'Locations':
				if (
					locationTypeValue === 'limited' &&
					!errors.locationName &&
					!errors.locationAddress &&
					!errors.locationRadius
				) {
					return (
						<div className="mt-2 space-y-2">
							<h1 className="text-body-small text-onPrimaryFixedVariant">
								{locationNameValue}
							</h1>
							<p className="text-neutral60 text-body-xx-small">
								{locationAddressValue}
							</p>
							<p className="text-neutral60 text-body-xx-small">
								{locationRadiusValue}km radius
							</p>
						</div>
					);
				}

				return null;

			case 'Time':
				if (
					timeTypeValue === TRAVEL_TIME_TYPE.custom &&
					startTimeValue &&
					endTimeValue &&
					daysValue
				) {
					return (
						<div className="mt-2.5">
							<h1 className="text-body-medium text-onPrimaryFixedVariant">
								{`${startTimeValue} - ${endTimeValue} (${timeDifference}) Hours`}
							</h1>
							<p className="uppercase text-neutral60 text-body-small">
								{`${daysValue.join(', ')}`}
							</p>
						</div>
					);
				}
				return null;
			default:
				return null;
		}
	};

	return (
		<button
			type="button"
			onClick={onClick}
			className="flex w-full items-center justify-between py-3 px-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
		>
			<div className="flex gap-2 w-1/2">
				<div className="mr-4">{icon}</div>
				<span className="text-body-medium text-base text-left text-neutral17">
					{title}
				</span>
			</div>

			<div className="w-1/2 text-left">
				<p className="text-neutral60 text-base text-body-medium">
					{value}
				</p>
				{description(title)}
			</div>

			<div className="flex items-center">
				<FaChevronRight className="h-3 text-gray-400" />
			</div>
		</button>
	);
};

export default RuleItem;
